package com.dynamicruntime.common.schema

import com.dynamicruntime.common.annotation.KdrPrivate
import com.dynamicruntime.common.exception.KdrException
import com.dynamicruntime.common.overlay.KeyedElement
import com.dynamicruntime.common.overlay.MergeRule
import com.dynamicruntime.common.overlay.MergeSpec
import com.dynamicruntime.common.overlay.OnNew
import com.dynamicruntime.common.util.Parsed
import com.dynamicruntime.common.util.Problem
import com.dynamicruntime.common.util.ProblemCode
import com.dynamicruntime.common.util.ProblemLocation
import com.dynamicruntime.common.util.TemplateIssue
import com.dynamicruntime.common.util.analyzeTemplate
import com.dynamicruntime.common.util.toJsonListOfMaps
import com.dynamicruntime.common.util.toJsonMapOrEmpty
import com.dynamicruntime.common.util.toOptStr

/**
 * The **field layout** for a schema type (issue #584): how a friendly form renders that type's fields, kept apart
 * from `SchType` because it varies by surface rather than by validity (`thoughts-schema-direction.md` §9, in
 * the private `sampwhite/Actions` design notes). It is authored inline in the schema document under the
 * `g-layout` keyword, but read out by its own function ([collectLayouts]) into this model, held **beside** the
 * compiled types in the schema store and **never** on `SchType`; it is stripped from the served schema
 * ([withoutLayouts]) and delivered out-of-band, so the wire schema stays a clean, documentation-grade artifact.
 *
 * What began as copy overrides ([SchLayoutField.label] / `description` / `hint`) and the block's
 * [fragmentFileId] has since grown the error override (#588), the default-presentation mode (#709), form-level
 * [strings] (#641), [mode] (#777) — authority over field order and membership, decided by the kernel's
 * `orderedFieldNames` — and a form's own requirements (#1022): a field it requires, a restated list of choices.
 * Those never change what data is valid ([formRequirementFailures]); they are how a workflow, which does not alter
 * schema, asks its form for more. The model is delivered to every friendly surface ([deliveredLayouts]) and parsed back on
 * the frontend ([parseDeliveredLayouts]), which now renders it.
 *
 * **Two things are called "layout"; always qualify which** (issue #834). This is the *field* layout: the fields
 * within one trait's data type. The *task* layout (`WfLayout`, on a workflow task) orders the traits within a
 * task, and is a placeholder until task-level presentation is designed. Both follow one definition: a layout
 * presents the members its level already defines -- it may order them, and a field layout may also annotate or
 * hide them for display -- but it never adds one.
 *
 * **One per type, for now.** Today a type has at most one field layout (a client may overlay it on their
 * variant). Selectors are to come down to this level -- choosing which of several field layouts applies, on
 * facts about the task and the viewer, as a task's display selector (issue #788) chooses its display -- so do
 * not build on the one-to-one as if it were permanent. The definition above holds for each candidate.
 *
 * Why a field layout has a [mode], and why an `authoritative` one may repeat what the schema already says, is
 * on [SLM].
 */
class SchLayout(
    /** The fragment file the block's `${'$'}{…}` substitutions resolve against, declared once for the block. */
    val fragmentFileId: String?,
    /**
     * The type's **name** (issues #605, #1534): one line, shown as the heading over the type as a whole, and -- for a
     * trait's data type -- the trait's name wherever it is named (`traitNameOf`, issue #1520). It shadows the data
     * type's `title`-as-heading default (`thoughts-workflow-layouts.md` §5). Inline Markdown, resolved like any layout
     * copy -- a `%{@t(...)}` backend fragment pull, a `${'$'}{…}` field value. Null leaves the schema `title`. The copy
     * under the heading is [description]; a field entry's `label` and `description` are the same pair for one field.
     */
    val label: String?,
    /** The per-field overrides, in declaration order. */
    val fields: List<SchLayoutField>,
    /**
     * The type's **form-level strings** (issue #641): overridable copy a form shows for the type as a whole
     * rather than for one field, keyed by [LAYSTR]. A surface holds a built-in default for each; a component
     * sets one here on the base type and a client overrides it on their variant, so the wording a consumer sees
     * can be altered without touching the surface. Empty when the layout says nothing about them, which reads as
     * "use the built-in copy" the same way an absent field override does.
     */
    val strings: Map<String, String> = emptyMap(),
    /**
     * How much authority this layout has over the presented field set and order (issue #777). [SchLayoutMode.overlay]
     * (the default) is the historic behavior -- annotate only. [SchLayoutMode.reorder] and
     * [SchLayoutMode.authoritative] hand the layout, respectively, the order and the order-plus-membership; the
     * render seam [orderedFieldNames] reads it, and the boot check enforces what each mode demands.
     */
    val mode: SchLayoutMode = SchLayoutMode.overlay,
    /**
     * The type's **introduction** (issue #1534): Markdown shown under its heading ([label]), on a workflow form above
     * the type's fields. Resolved like the label. Null for none.
     */
    val description: String? = null,
) : JsonMappable {
    /** The schema properties this layout addresses -- what the boot check holds against the type. */
    val fieldNames: List<String> = fields.map { it.field }

    /** The override for property [name], or null when this layout does not address that field. */
    fun fieldFor(name: String): SchLayoutField? = fields.firstOrNull { it.field == name }

    /**
     * The layout as its `g-layout` block again -- the wire form (issue #585). Delivery re-serializes the
     * **model** rather than shipping the authored block, so what a page receives is what the store holds: the
     * pruned form for a client that narrowed the type, never a field the client's type lacks. Round-trips
     * through [parseSchLayout]; a null override is omitted, not written as null. Built once and kept: the
     * model never changes after boot and every catalog and workflow-view request delivers it, so the wire form
     * is a property of the layout rather than work done per request (the same reason `servedDefs` is).
     */
    private val jsonMap: Map<String, Any?> by lazy {
        val out = LinkedHashMap<String, Any?>()
        fragmentFileId?.let { out[SL.fragmentFileId] = it }
        label?.let { out[SL.label] = it }
        description?.let { out[SL.description] = it }
        // Written only when it departs from the default, so an overlay layout's wire form is byte-identical to
        // before issue #777 -- the same reason a null field override is omitted rather than written as null.
        if (mode != SchLayoutMode.overlay) out[SL.mode] = mode.name
        if (strings.isNotEmpty()) out[SL.strings] = strings
        // A heading-only layout has no field list to write (issue #1070); written empty, it would not parse back.
        if (fields.isNotEmpty()) out[SL.schemaFields] = fields.map { it.toJsonMap() }
        out
    }

    override fun toJsonMap(): Map<String, Any?> = jsonMap

    /**
     * This layout with every field the type does not declare dropped -- [props] being the type's property
     * names. A client that narrows a type (mentions only the properties it keeps) inherits the base's layout
     * intact, so the inherited layout can name a field the client's type no longer has; the field is moot for
     * that client, and pruning it is the sanctioned outcome rather than a refused boot. Returns `this` when
     * nothing is dropped.
     */
    fun prunedTo(props: Set<String>): SchLayout {
        val kept = fields.filter { it.field in props }
        return if (kept.size == fields.size) this else SchLayout(fragmentFileId, label, kept, strings, mode, description)
    }
}

/** One field's overrides in a [SchLayout] (issue #584): the schema property [field] it addresses, and the copy
 *  that shadows the schema's own `title` / `description` / bound hint when the form renders it. */
class SchLayoutField(
    val field: String,
    val label: String?,
    val description: String?,
    val hint: String?,
    /**
     * The **error override** (issue #588): the form's message for a failure against this field, keyed by
     * [SchFailCode] name (plus `default`), shadowing the framework's built-in wording and the `g-errors`
     * validator floor (issue #589) when present. Resolved on the frontend and `${'$'}{…}`-capable over the
     * per-failure-code params ([errorContextNames]), the same machinery the templated hint uses -- this is
     * where message *variation* is homed, without overlaying a whole property to change one error key. Empty
     * when the layout says nothing about this field's errors, which reads as "fall through" the same way a null
     * copy override does. Keys are boot-checked against the code enum, so a mistyped key fails the boot.
     */
    val errors: Map<String, String> = emptyMap(),
    /**
     * How a **supplied default** for this field is presented (issue #709) -- one of [SLDM] (`filled`, shown in
     * the control and marked; or `offer`, an empty control beside a "use it" affordance). A supplied default is
     * any value the backend hands the form that a person did not enter -- today a `prefillData` default
     * (`source=prefill`), later other sources -- so this is presentation about the *field*, not tied to one
     * source. Null leaves the surface's own default (`filled`); the mode a field never defaults is simply unset.
     */
    val defaultMode: String? = null,
    /**
     * A **form requirement** (issue #1022): this form asks for the field, though the schema may not. It never
     * changes what data is valid -- see [formRequirementFailures] -- and only adds: a layout cannot make optional
     * a field the schema requires, so the key is `true` or absent.
     */
    val required: Boolean = false,
    /**
     * The choices this form offers (issue #1022), **restated** whole: which of the schema's choices, in what order,
     * and their copy on this form. Null leaves the schema's list as it is. Each value is one the schema offers; the
     * form shows only those the schema offers for the data entered so far ([offeredChoices]).
     */
    val choices: List<SchLayoutChoice>? = null,
    /**
     * Whether the field is **long text** (issue #1118): a text area rather than one line. Presentation only -- the
     * data is a plain string either way -- so it lives in the layout, never in the schema. Only a plain text field
     * takes it ([layoutFieldProblems]).
     */
    val multiline: Boolean = false,
) : JsonMappable {
    /** The entry as written in a `schemaFields` list; see [SchLayout.toJsonMap]. */
    override fun toJsonMap(): Map<String, Any?> {
        val out = LinkedHashMap<String, Any?>()
        out[SL.field] = field
        label?.let { out[SL.label] = it }
        description?.let { out[SL.description] = it }
        hint?.let { out[SL.hint] = it }
        if (errors.isNotEmpty()) out[SL.errors] = errors
        defaultMode?.let { out[SL.defaultMode] = it }
        if (required) out[SL.required] = true
        choices?.let { list -> out[SL.choices] = list.map { it.toJsonMap() } }
        if (multiline) out[SL.multiline] = true
        return out
    }
}

/**
 * One choice a form offers in a [SchLayoutField.choices] list (issue #1022): a [value] the schema offers, with the
 * form's own [label] and [description] for it -- each absent to keep the schema's label, or no description. The
 * shape the later verbose choice display grows (an extended description, an image): the strict parser makes each
 * an added key.
 */
class SchLayoutChoice(val value: String, val label: String? = null, val description: String? = null) : JsonMappable {
    override fun toJsonMap(): Map<String, Any?> {
        val out = LinkedHashMap<String, Any?>()
        out[SL.value] = value
        label?.let { out[SL.label] = it }
        description?.let { out[SL.description] = it }
        return out
    }
}

/**
 * Declares a type's `g-layout` from the schema DSL (issue #585): `layout { field("topic", label = "Topic") }`
 * inside a `type("X") { ... }` block, or a trait's data block. Writes the same block [parseSchLayout] reads, so
 * the boot check and the delivery see a handwritten block and a built one identically. A builder rather than
 * the raw-map escape hatch because the sample's layouts are read by people, and the sets [SL.blockKeys] /
 * [SL.fieldKeys] the parser is strict about are then spelled once, here.
 *
 * A [comment] on the block or on a field (issue #1135) is written into the block as its `$comment` and is not part
 * of the [SchLayout] model -- see [SL.blockKeys].
 */
class SchLayoutBuilder(
    private val fragmentFileId: String?,
    private val label: String? = null,
    private val mode: SchLayoutMode = SchLayoutMode.overlay,
    private val comment: String? = null,
    private val description: String? = null,
) {
    private val fields = mutableListOf<SchLayoutField>()
    private val fieldComments = HashMap<String, String>()
    private val strings = LinkedHashMap<String, String>()

    /** A form-level layout string (issue #641), keyed by [LAYSTR]: the copy this type's forms show for the
     *  message named by [key], overriding the surface's built-in default. */
    fun string(key: String, value: String) {
        strings[key] = value
    }

    /** One field's overrides; each is optional. The [errors] block (issue #588) declares the form's wording per
     *  [SchFailCode], reusing `g-errors`' own [SchErrors] builder so the named-per-code functions are identical.
     *  [required] and [choices] are the form's requirements (issue #1022). */
    fun field(
        name: String,
        label: String? = null,
        description: String? = null,
        hint: String? = null,
        defaultMode: String? = null,
        required: Boolean = false,
        choices: List<SchLayoutChoice>? = null,
        comment: String? = null,
        multiline: Boolean = false,
        errors: (SchErrors.() -> Unit)? = null,
    ) {
        comment?.let { fieldComments[name] = it }
        val errMap = errors?.let { block ->
            val data = LinkedHashMap<String, Any?>()
            SchErrors(data).block()
            data.mapValues { it.value.toOptStr().orEmpty() }
        } ?: emptyMap()
        fields.add(SchLayoutField(name, label, description, hint, errMap, defaultMode, required, choices, multiline))
    }

    /** The finished block, as the JSON `g-layout` value, with each comment beside what it is about. */
    fun build(): Map<String, Any?> {
        val built = SchLayout(fragmentFileId, label, fields.toList(), strings.toMap(), mode, description).toJsonMap()
        if (comment == null && fieldComments.isEmpty()) return built
        val out = LinkedHashMap<String, Any?>()
        comment?.let { out[SCH.dComment] = it }
        for ((key, value) in built) {
            // A field's comment goes right after the `field` naming it: `+` keeps the left map's order.
            out[key] = if (key != SL.schemaFields) value else value.toJsonListOfMaps().map { entry ->
                val note = fieldComments[entry[SL.field]] ?: return@map entry
                linkedMapOf(SL.field to entry[SL.field], SCH.dComment to note) + entry
            }
        }
        return out
    }
}

/**
 * Attaches a `g-layout` to the type being built; see [SchLayoutBuilder]. Replaces one declared earlier. [label] is the
 * type's name, one line, and [description] the introduction under it (issue #1534). [mode] (issue #777) hands the
 * layout authority over the field order ([SchLayoutMode.reorder]) or the order and membership
 * ([SchLayoutMode.authoritative]); the default [SchLayoutMode.overlay] only annotates.
 */
fun SchTypeBuilder.layout(
    fragmentFileId: String? = null,
    label: String? = null,
    mode: SchLayoutMode = SchLayoutMode.overlay,
    comment: String? = null,
    description: String? = null,
    block: SchLayoutBuilder.() -> Unit,
) {
    data[SCH.layout] = SchLayoutBuilder(fragmentFileId, label, mode, comment, description).apply(block).build()
}

/**
 * The `{ typeName -> g-layout block }` to deliver beside a served `$defs` closure (issue #585): the layouts in
 * [layouts] for exactly the [typeNames] the closure carries, each in wire form ([SchLayout.toJsonMap]). A type with
 * no layout has **no entry** -- absent, not empty -- so a page reads "no layout" and "no key" the same way. The
 * shared helper both friendly surfaces call (the endpoint catalog and the workflow view), the way the delivered
 * cfacts have one shape; a pure function, so the frontend could reuse it if it ever assembles a view itself.
 */
fun deliveredLayouts(layouts: Map<String, SchLayout>, typeNames: Collection<String>): Map<String, Any?> {
    val out = LinkedHashMap<String, Any?>()
    for (name in typeNames) {
        layouts[name]?.let { out[name] = it.toJsonMap() }
    }
    return out
}

/**
 * The frontend's read of a delivered `layouts` map (issue #585) -- the inverse of [deliveredLayouts], through
 * the same strict [parseSchLayout] the boot ran, so a page holds the same model the store did. An absent or
 * non-object map (an older node) parses to empty, which reads as "no layouts" rather than a fault: the schema
 * renders without one. A malformed entry, though, is a fault -- the backend built it from a parsed model, so a
 * bad one is a wiring bug worth surfacing, not a case to render around.
 */
fun parseDeliveredLayouts(raw: Any?): Map<String, SchLayout> {
    val map = raw.toJsonMapOrEmpty()
    val out = LinkedHashMap<String, SchLayout>()
    for ((name, block) in map) {
        out[name] = parseSchLayout("Type '$name'", block.toJsonMapOrEmpty())
    }
    return out
}

/** The vocabulary inside a `g-layout` block (issue #584). Bare rather than `g-`-prefixed: these are fields
 *  inside the value of the `g-layout` keyword, not keywords in the schema namespace, so nothing collides. The
 *  sets [blockKeys] and [fieldKeys] are what the parser refuses anything outside of, so a misspelled or
 *  not-yet-supported key fails the boot rather than silently rendering nothing. */
@Suppress("ConstPropertyName")
object SL {
    /** The per-field override list (renamed from the draft's `formFields`: it lists schema properties). */
    const val schemaFields = "schemaFields"

    /** On a [schemaFields] entry: the schema property it addresses. */
    const val field = "field"

    /** On a [schemaFields] entry: the label that shadows the schema's `title`. */
    const val label = "label"

    /** On a [schemaFields] entry: the description that shadows the schema's `description`. */
    const val description = "description"

    /** On a [schemaFields] entry: the hint that shadows the derived bound hint. */
    const val hint = "hint"

    /** On a [schemaFields] entry: the error override (issue #588) -- a `{ SchFailCode-name -> message }` block
     *  (plus `default`), the form's wording for a failure against the field, shadowing the built-in message. */
    const val errors = "errors"

    /** On a [schemaFields] entry: how a supplied default for the field is presented (issue #709); one of [SLDM]. */
    const val defaultMode = "defaultMode"

    /** On a [schemaFields] entry: `true` when this form asks for the field (issue #1022) -- a form requirement. */
    const val required = "required"

    /** On a [schemaFields] entry: the choices this form offers, restated (issue #1022); each a [choiceKeys] object. */
    const val choices = "choices"

    /** On a [schemaFields] entry: `true` when the field is long text, drawn as a text area (issue #1118). */
    const val multiline = "multiline"

    /** On a [choices] entry: the schema's value it offers. Its [label] and [description] are the form's copy for it. */
    const val value = "value"

    /** On the block: the fragment file its `${'$'}{…}` substitutions resolve against. */
    const val fragmentFileId = "fragmentFileId"

    /** On the block: the form-level strings map (issue #641) -- a `{ LAYSTR-name -> copy }` block of overridable
     *  wording the form shows for the type as a whole, not for one field. */
    const val strings = "strings"

    /** On the block: how much authority the layout has over the presented field set and order (issue #777); one
     *  of [SLM]. Absent means [SLM.overlay] -- the layout only annotates, and the schema owns order and set. */
    const val mode = "mode"

    // Note: [label] and [description] are both block keys (the type's name and the introduction under it, issues #605,
    // #1534) and field keys (a property's label and description); the two levels are told apart by nesting, not by
    // name -- the same pair of words at either level (`thoughts-workflow-layouts.md` §5).

    // A block and a field entry also take a `$comment` (issue #1135): an author's note on why the layout narrows a
    // list or takes over a type, kept in the authored block and never read into the model, so it is stored with the
    // schema it sits in and never delivered to a page. A choice entry and the strings map are copy, and take none.

    /** Every key a `g-layout` block may carry. */
    val blockKeys: Set<String> = setOf(schemaFields, fragmentFileId, label, description, strings, mode, SCH.dComment)

    /** Every key a [schemaFields] entry may carry. */
    val fieldKeys: Set<String> =
        setOf(field, label, description, hint, errors, defaultMode, required, choices, multiline, SCH.dComment)

    /** Every key a [choices] entry may carry. */
    val choiceKeys: Set<String> = setOf(value, label, description)
}

/**
 * How a type's field layout merges with an alteration of it (issue #985) -- the layout being a resource of its own,
 * keyed by its type's name, rather than a third level of the type's body.
 *
 * **The alteration's own [SL.mode] says which kind of change it is.**
 *
 *  - **None, or [SLM.overlay]: a copy change.** [SL.schemaFields] is keyed by [SL.field], and an alteration's entry
 *    replaces the inherited entry for that field whole -- so a client rewording one field states that field's entry
 *    alone and every other entry is inherited. An entry for a field the inherited layout does not list is appended
 *    where the layout only annotates (mode [SLM.overlay], the alteration's or else the inherited one's), and refused
 *    where order matters ([SLM.reorder], [SLM.authoritative]): it would need a place, which an anchor would give, and
 *    there are none yet -- the message says to restate the list instead.
 *  - **[SLM.reorder] or [SLM.authoritative]: an order or membership change.** [SL.schemaFields] is **restated**
 *    ([MergeRule.RestateKeyed]): the alteration's list is the order, and under [SLM.authoritative] the membership too;
 *    an entry naming only its `field` keeps the inherited entry for that field.
 *
 * (A field must still be one the type declares; the layout check says so, on the merged result.) [SL.strings]
 * merges by key; every other block key (the heading [SL.label], [SL.fragmentFileId], [SL.mode]) replaces.
 */
fun layoutMergeSpec(base: Map<String, Any?>, overlay: Map<String, Any?>): MergeSpec {
    val ownMode = overlay[SL.mode] as? String
    val fields = if (ownMode == SLM.reorder || ownMode == SLM.authoritative) {
        MergeRule.RestateKeyed(SL.field)
    } else {
        val mode = ownMode ?: base[SL.mode] as? String ?: SLM.overlay
        MergeRule.Keyed(
            SL.field, KeyedElement.replace, if (mode == SLM.overlay) OnNew.append else OnNew.refuse,
            refusalHint = "To change which fields the layout lists, or their order, set its '${SL.mode}' to " +
                "'${SLM.reorder}' or '${SLM.authoritative}' and restate the list.",
        )
    }
    return MergeSpec(mapOf(SL.schemaFields to fields, SL.strings to MergeRule.Merge))
}

/**
 * The values a field's [SL.defaultMode] may take (issue #709) -- how the form presents a **supplied default**
 * (a value the backend hands the form that a person did not enter). A closed set, so a typo fails the boot; the
 * surface's own fallback when a field says nothing is [filled], so a layout carries the mode only to override it.
 *
 * The mode is deliberately about the *field*, not the *source* -- `prefill` is the first supplied source, and
 * others (a Salesforce/Excel pull) follow -- so nothing here mentions `prefill`.
 */
@Suppress("ConstPropertyName")
object SLDM {
    /** The default is shown in the control immediately, marked as a default. Low-friction; the fallback mode. */
    const val filled = "filled"

    /** The control stays empty and a "use it" affordance offers the value; clicking commits it as entered. */
    const val offer = "offer"

    /** The closed set of modes; a value outside it fails the boot. */
    val values: Set<String> = setOf(filled, offer)
}

/**
 * The wire values of the field layout's [SL.mode] (issue #777): how much authority the layout has over which of
 * a type's fields are presented, and in what order. A closed set resolved onto [SchLayoutMode]; an unrecognized
 * value fails the boot. The default when the key is absent is [overlay] -- today's behavior.
 *
 * **Why there is a mode: two kinds of author** (issue #834). The *form author* treats a form as a convenient
 * way to enter an endpoint call's data: they live with the schema's defaults and tweak some copy or order, which
 * is [overlay] (or [reorder]). The *application builder* wants full control of how workflows and forms render,
 * and treats the field layout as the **source of truth for every GUI decision**, which is [authoritative]. For
 * them, repeating what the schema already says -- listing every field, even in exactly the schema's order -- is
 * intended rather than redundant: every GUI decision is then findable in the layout. The boot's completeness
 * check ([layoutFieldProblems]) is what keeps that duplication safe when the schema later gains a required field.
 *
 * **Guidance, not a rule:** a change that does not alter a schema's API semantics (order, copy) belongs in the
 * field layout. Reordering properties in a schema overlay still works; the schema's property order is the
 * *default* presentation order, and the payload order.
 *
 * All three **narrow and order**; none can *widen*. A field the schema hides (a `g-derived` value in a
 * friendly form, a `g-visibleWhen` field the caller's cfacts fail, a conditionally-forbidden field) stays
 * hidden even when the layout lists it -- the layout selects and orders among the fields the schema is willing
 * to show, it does not overrule the schema's own gates.
 */
@Suppress("ConstPropertyName", "unused")
object SLM {
    /** The layout only annotates: the schema owns the field set and their order, and the layout supplies copy
     *  for the fields it happens to address. The default, and every layout's behavior before issue #777. */
    const val overlay = "overlay"

    /** The layout owns the **order** of the fields it lists (drawn first, in list order); every other declared
     *  field still renders, appended in schema order. Nothing is hidden, so no completeness rule applies. */
    const val reorder = "reorder"

    /** The layout owns the field set **and** their order: only the fields it lists render, in list order. The
     *  boot then requires the list to name every person-entered required property (see [layoutFieldProblems]),
     *  since a required field the form omits could never be supplied. */
    const val authoritative = "authoritative"

    /** The closed set; a value outside it fails the boot. */
    val values: Set<String> = setOf(overlay, reorder, authoritative)
}

/**
 * The resolved [SL.mode] of a layout (issue #777), from its [SLM] wire value. An enum because it is a closed
 * operational set the render seam (`orderedFieldNames`) switches on. Lower-case-first to match the wire spelling.
 */
@Suppress("EnumEntryName")
enum class SchLayoutMode {
    overlay,
    reorder,
    authoritative,
}

/**
 * Parses one `g-layout` block [raw] into a [SchLayout], throwing on a structurally bad one so a mistake fails
 * the boot rather than rendering as nothing -- the throwing form of [parseSchLayoutResult], raising its first
 * problem. [where] names the type for the message.
 */
fun parseSchLayout(where: String, raw: Map<String, Any?>): SchLayout =
    parseSchLayoutResult(where, raw).orThrow { KdrException(it) }

/**
 * Parses one `g-layout` block [raw] (issue #909): the [SchLayout], or every problem with the block, each coded
 * ([LayoutError]) and located within it (`schemaFields[2].defaultMode`). Strict about keys: an unknown key on the
 * block or on a field entry is refused (the same stance `g-errors` takes), because the failure it guards against
 * is a block that parses clean and does nothing -- the draft's `formFields`, a `schemaFields` written as an
 * object, a typo. A present block must say something for the same reason: at least one field, or -- with no field
 * list -- the type's name ([SL.label]), its introduction ([SL.description]), or form [SL.strings] (issue #1070), which a
 * type with no field copy may still be given ([layoutSaysSomething]). A list that is present must not be empty, and a
 * [SL.mode] that orders or chooses fields needs one. The name is one line: copy under it is the introduction (#1534).
 * [where] names the type for the message.
 */
fun parseSchLayoutResult(where: String, raw: Map<String, Any?>): Parsed<SchLayout> {
    val problems = mutableListOf<Problem>()
    unknownKeysProblem(where, "a '${SCH.layout}' block", raw.keys, SL.blockKeys, path = null)?.let { problems.add(it) }
    val entries = raw[SL.schemaFields]
    if (if (entries == null) !layoutSaysSomething(raw) else entries !is List<*> || entries.isEmpty()) {
        problems.add(
            layoutProblem(
                LayoutError.noFields,
                "$where: a '${SCH.layout}' block must list at least one '${SL.schemaFields}' entry, or -- with no " +
                    "'${SL.mode}' -- give the type a name ('${SL.label}'), an introduction ('${SL.description}'), or form " +
                    "'${SL.strings}'.",
                SL.schemaFields,
            ),
        )
    }
    val fields = (entries as? List<*>).toJsonListOfMaps().mapIndexedNotNull { i, m ->
        val at = "${SL.schemaFields}[$i]"
        unknownKeysProblem(where, "a '${SL.schemaFields}' entry", m.keys, SL.fieldKeys, at)?.let { problems.add(it) }
        val field = m[SL.field].toOptStr() ?: run {
            problems.add(
                layoutProblem(
                    LayoutError.noFieldName, "$where: a '${SL.schemaFields}' entry has no '${SL.field}'.", at,
                ),
            )
            return@mapIndexedNotNull null
        }
        // The error override reuses `g-errors`' own reader (issue #588): the same key validation against the
        // SchFailCode enum, and the same reserved-object-form tolerance, so the two ways to key a message off a
        // failure code cannot drift on what a valid key is.
        val errors = when (val read = readErrorMessages(m[SL.errors], "$where field '$field'")) {
            is Parsed.Ok -> read.value
            is Parsed.Failed -> {
                read.problems.forEach { p ->
                    val key = p.location?.path
                    problems.add(layoutProblem(LayoutError.badValue, p.message, "$at.${SL.errors}.$key"))
                }
                emptyMap()
            }
        }
        val defaultMode = m[SL.defaultMode].toOptStr()?.takeIf {
            (it in SLDM.values).also { known ->
                if (!known) {
                    problems.add(
                        layoutProblem(
                            LayoutError.badValue,
                            "$where: field '$field' has '${SL.defaultMode}' '$it', not one of ${SLDM.values.sorted()}.",
                            "$at.${SL.defaultMode}",
                        ),
                    )
                }
            }
        }
        // A form requirement only adds (issue #1022): `true`, or absent. `false` would read as making a field optional,
        // which a layout cannot do, so it is refused rather than ignored.
        val required = when (val r = m[SL.required]) {
            null, true -> r == true
            else -> {
                problems.add(
                    layoutProblem(
                        LayoutError.badValue,
                        "$where: field '$field' has '${SL.required}' $r; a layout only adds a requirement, so it is " +
                            "true or absent.",
                        "$at.${SL.required}",
                    ),
                )
                false
            }
        }
        val choices = readLayoutChoices(where, field, m[SL.choices], "$at.${SL.choices}", problems)
        // Long text is on or absent, as a requirement is: `false` says nothing a missing key does not.
        val multiline = when (val r = m[SL.multiline]) {
            null, true -> r == true
            else -> {
                problems.add(
                    layoutProblem(
                        LayoutError.badValue,
                        "$where: field '$field' has '${SL.multiline}' $r; it is true or absent.",
                        "$at.${SL.multiline}",
                    ),
                )
                false
            }
        }
        SchLayoutField(
            field, m[SL.label].toOptStr(), m[SL.description].toOptStr(), m[SL.hint].toOptStr(), errors, defaultMode,
            required, choices, multiline,
        )
    }
    val strings = readLayoutStrings(where, raw[SL.strings], problems)
    val mode = when (val modeStr = raw[SL.mode].toOptStr()) {
        null -> SchLayoutMode.overlay
        SLM.overlay -> SchLayoutMode.overlay
        SLM.reorder -> SchLayoutMode.reorder
        SLM.authoritative -> SchLayoutMode.authoritative
        else -> {
            problems.add(
                layoutProblem(
                    LayoutError.badValue,
                    "$where: a '${SCH.layout}' '${SL.mode}' is '$modeStr', not one of ${SLM.values.sorted()}.",
                    SL.mode,
                ),
            )
            SchLayoutMode.overlay
        }
    }
    val label = raw[SL.label].toOptStr()
    if (label != null && ('\n' in label || '\r' in label)) {
        problems.add(
            layoutProblem(
                LayoutError.badValue,
                "$where: a '${SCH.layout}' '${SL.label}' is the type's name, one line; put the copy under it in " +
                    "'${SL.description}'.",
                SL.label,
            ),
        )
    }
    if (problems.isNotEmpty()) return Parsed.Failed(problems)
    return Parsed.Ok(SchLayout(raw[SL.fragmentFileId].toOptStr(), label, fields, strings, mode, raw[SL.description].toOptStr()))
}

/**
 * Whether the `g-layout` block [raw] says anything a form would show (issue #1070): a field entry, or -- under the
 * default [SLM.overlay] mode, written or not, since a mode that orders or chooses fields needs a list -- a non-blank
 * name ([SL.label]) or introduction ([SL.description]), or form [SL.strings]. A block that says nothing is refused by
 * the parser, so an edit that empties one removes the block instead. Pure.
 */
fun layoutSaysSomething(raw: Map<String, Any?>): Boolean {
    if ((raw[SL.schemaFields] as? List<*>)?.isNotEmpty() == true) return true
    val mode = raw[SL.mode]
    return (mode == null || mode == SLM.overlay) &&
        (
            !raw[SL.label].toOptStr().isNullOrBlank() || !raw[SL.description].toOptStr().isNullOrBlank() ||
                (raw[SL.strings] as? Map<*, *>)?.isNotEmpty() == true
            )
}

/**
 * Reads a `g-layout` block's `strings` map (issue #641): a `{ LAYSTR-name -> copy }` object, adding to [problems]
 * what is wrong with it. Absent reads as empty; a non-object, or a key outside [LAYSTR.keys], or a non-string
 * value, is a problem, the same strictness the block's other keys take -- a typo'd string name would otherwise
 * render nothing.
 */
private fun readLayoutStrings(where: String, raw: Any?, problems: MutableList<Problem>): Map<String, String> {
    if (raw == null) return emptyMap()
    if (raw !is Map<*, *>) {
        problems.add(layoutProblem(LayoutError.badValue, "$where: '${SL.strings}' must be an object.", SL.strings))
        return emptyMap()
    }
    val map = raw.toJsonMapOrEmpty()
    unknownKeysProblem(where, "a '${SL.strings}' block", map.keys, LAYSTR.keys, SL.strings)?.let { problems.add(it) }
    val out = LinkedHashMap<String, String>()
    for ((key, value) in map) {
        // A genuine string, not a coerced one: a number or boolean here is a mistake, and silently rendering
        // "5" is the "parses clean, renders wrong" the layout checks exist to prevent.
        if (value is String) {
            out[key] = value
        } else {
            problems.add(
                layoutProblem(
                    LayoutError.badValue,
                    "$where: the '${SL.strings}' value for '$key' must be a string.",
                    "${SL.strings}.$key",
                ),
            )
        }
    }
    return out
}

/**
 * Reads a field entry's `choices` list (issue #1022), adding to [problems] what is wrong with it: absent reads as
 * null (the schema's list stands); otherwise a non-empty list of objects, each with a non-blank `value` that no other
 * entry repeats, and only [SL.choiceKeys]. Whether each value is one the schema offers is the load check's
 * ([layoutFieldProblems]), which holds the type.
 */
private fun readLayoutChoices(where: String, field: String, raw: Any?, at: String, problems: MutableList<Problem>): List<SchLayoutChoice>? {
    if (raw == null) return null
    if (raw !is List<*> || raw.isEmpty()) {
        problems.add(layoutProblem(LayoutError.badValue, "$where: field '$field' has '${SL.choices}' that is not a non-empty list.", at))
        return null
    }
    val seen = HashSet<String>()
    return raw.mapIndexedNotNull { i, element ->
        val entryAt = "$at[$i]"
        val m = (element as? Map<*, *>)?.toJsonMapOrEmpty() ?: run {
            problems.add(layoutProblem(LayoutError.badValue, "$where: field '$field' has a choice that is not an object.", entryAt))
            return@mapIndexedNotNull null
        }
        unknownKeysProblem(where, "a '${SL.choices}' entry", m.keys, SL.choiceKeys, entryAt)?.let { problems.add(it) }
        val value = m[SL.value] as? String
        if (value.isNullOrBlank() || !seen.add(value)) {
            val why = if (value.isNullOrBlank()) "has no '${SL.value}'" else "repeats the value '$value'"
            problems.add(layoutProblem(LayoutError.badValue, "$where: field '$field' has a choice that $why.", "$entryAt.${SL.value}"))
            return@mapIndexedNotNull null
        }
        SchLayoutChoice(value, m[SL.label].toOptStr(), m[SL.description].toOptStr())
    }
}

private fun unknownKeysProblem(
    where: String,
    what: String,
    present: Set<String>,
    allowed: Set<String>,
    path: String?,
): Problem? {
    val unknown = present.filterNot { it in allowed }
    if (unknown.isEmpty()) return null
    return layoutProblem(
        LayoutError.unknownKey,
        "$where: $what has unknown key(s) ${unknown.sorted()}; allowed: ${allowed.sorted()}.",
        path,
    )
}

/**
 * The `{ typeName -> SchLayout }` for every type in [defs] that declares a `g-layout` (issue #584). A read-only
 * pass -- [defs] is not mutated, so a body keeps its `g-layout` for the per-client overlay merge to inherit;
 * the served schema is cleaned separately by [withoutLayouts]. Throws on the first type whose layout
 * [parseTypeLayout] finds a problem with.
 */
fun collectLayouts(defs: Map<String, Any?>): Map<String, SchLayout> {
    val out = LinkedHashMap<String, SchLayout>()
    for ((name, body) in defs) {
        if (body !is Map<*, *>) continue
        parseTypeLayout(name, body).orThrow { KdrException(it) }?.let { out[name] = it }
    }
    return out
}

/**
 * The layout one type [name]'s [body] declares (issue #909): null when it declares none, or every problem with it.
 *
 * Only a **named** type's own top-level `g-layout` is collected. A `g-layout` found anywhere below that -- on
 * an inline sub-object property, say -- is **refused**, not ignored: there is no name to key it by, so it could
 * be neither delivered nor checked, and the honest answer is to say so at boot ("pull the sub-object out as a
 * named type") rather than let it silently render nothing and leak into the served schema. A `g-layout` that
 * is not an object is refused too. A nested one is located by its path in [body]; the rest within the block.
 */
fun parseTypeLayout(name: String, body: Map<*, *>): Parsed<SchLayout?> {
    val where = "Type '$name'"
    val nested = mutableListOf<Problem>()
    findNestedLayouts(where, body, depth = 0, path = "", out = nested)
    if (nested.isNotEmpty()) return Parsed.Failed(nested)
    val rawLayout = body[SCH.layout] ?: return Parsed.Ok(null)
    if (rawLayout !is Map<*, *>) {
        return Parsed.failed(LayoutError.badValue, "$where: '${SCH.layout}' must be an object.")
    }
    return parseSchLayoutResult(where, rawLayout.toJsonMapOrEmpty())
}

/** The deepest a schema body is walked looking for a stray nested `g-layout`; matches the JSON nesting cap. */
private const val maxLayoutScanDepth = 50

/** Adds to [out] each `g-layout` key found at any depth **below** the top level of [node]; [depth] guards the walk. */
private fun findNestedLayouts(where: String, node: Map<*, *>, depth: Int, path: String, out: MutableList<Problem>) {
    if (depth >= maxLayoutScanDepth) return
    for ((key, value) in node) {
        // The top-level key is the one collected; anything under it is not.
        if (depth == 0 && key == SCH.layout) continue
        val at = childPath(path, key.toString())
        when (value) {
            is Map<*, *> -> {
                if (value.containsKey(SCH.layout)) {
                    out.add(
                        Problem(
                            LayoutError.nestedLayout,
                            "$where: a '${SCH.layout}' under '$key' is not on a named type, so it can be neither " +
                                "delivered nor checked. Pull the sub-object out as a named type and put the layout there.",
                            ProblemLocation(path = at),
                        ),
                    )
                }
                findNestedLayouts(where, value, depth + 1, at, out)
            }
            is List<*> -> value.forEachIndexed { i, element ->
                if (element is Map<*, *>) {
                    if (element.containsKey(SCH.layout)) {
                        out.add(
                            Problem(
                                LayoutError.nestedLayout,
                                "$where: a '${SCH.layout}' inside '$key' is not on a named type, so it can be " +
                                    "neither delivered nor checked. Pull the sub-object out as a named type.",
                                ProblemLocation(path = "$at[$i]"),
                            ),
                        )
                    }
                    findNestedLayouts(where, element, depth + 1, "$at[$i]", out)
                }
            }
        }
    }
}

/**
 * [defs] with each body's top-level `g-layout` removed (issue #584) -- the served, documentation-grade schema,
 * since the layout is delivered out-of-band. Non-destructive: bodies that carry a `g-layout` are copied without
 * it, everything else is returned as-is. Top-level is the whole job: [collectLayouts] has already refused any
 * `g-layout` below it at boot, so none can be here to miss. Shape is validated by [collectLayouts] too; this
 * only strips.
 */
fun withoutLayouts(defs: Map<String, Any?>): Map<String, Any?> = defs.mapValues { (_, body) ->
    if (body is Map<*, *> && body.containsKey(SCH.layout)) body.toJsonMapOrEmpty() - SCH.layout else body
}

/**
 * The problems with [layout] against the [type] it is declared on (issue #584) -- the boot check. A layout
 * belongs on an **object** type: a union carries its fields on its branches and an array on its items, so
 * there is no single property set to render, and a layout there is refused with a message naming that rather
 * than listing every field as undeclared. On an object type, each field the layout names must be a property
 * the type declares. An unresolved type is itself a problem. [where] names the type.
 */
fun layoutFieldProblems(where: String, layout: SchLayout, type: SchType?): List<Problem> {
    if (type == null) {
        return listOf(
            layoutProblem(
                LayoutError.unresolvedType, "$where: a '${SCH.layout}' is declared on a type that did not resolve.",
            ),
        )
    }
    if (type.variants != null || type.jsonType == SCT.array) {
        return listOf(
            layoutProblem(
                LayoutError.notAnObjectType,
                "$where: a '${SCH.layout}' belongs on an object type; this is a " +
                    "${if (type.variants != null) "union" else "array"} -- put the layout on the branch or item type.",
            ),
        )
    }
    val props = type.properties.keys
    val undeclared = layout.fields.mapIndexedNotNull { i, f ->
        if (f.field in props) return@mapIndexedNotNull null
        layoutProblem(
            LayoutError.undeclaredField,
            "$where: '${SCH.layout}' names field '${f.field}', which the type does not declare.",
            "${SL.schemaFields}[$i].${SL.field}",
        )
    }
    if (undeclared.isNotEmpty()) return undeclared

    return formRequirementProblems(where, layout, type) + multilineProblems(where, layout, type) +
        authoritativeLayoutProblems(where, layout, type)
}

/**
 * The fields [layout] marks long text (issue #1118) that are not plain text in [type]: a number, a choice, a date,
 * or a file has no use for a text area, and drawing one would invite text the field refuses.
 */
fun multilineProblems(where: String, layout: SchLayout, type: SchType): List<Problem> =
    layout.fields.withIndex().mapNotNull { (i, field) ->
        val prop = type.properties[field.field]?.takeIf { field.multiline } ?: return@mapNotNull null
        if (isPlainText(prop.valueType)) return@mapNotNull null
        layoutProblem(
            LayoutError.multilineNotText,
            "$where: '${SCH.layout}' marks field '${field.field}' long text, but it is not a plain text field.",
            "${SL.schemaFields}[$i].${SL.multiline}",
        )
    }

/** Whether [type] is plain text: a string with no choices and no date or binary format -- what a text area can enter. */
fun isPlainText(type: SchType): Boolean =
    type.jsonType == SCT.string && type.options == null && !isDateFormat(type.format) && !isBinaryFormat(type.format)

/**
 * The problems with [layout]'s **form requirements** against [type] (issue #1022):
 *  - `choices` on a field the schema gives no closed list of choices, or naming a value the schema does not offer --
 *    a form restates the schema's choices; it cannot make up its own;
 *  - `required` on a field nobody at the form could fill in: one the server derives, or one a `g-visibleWhen` hides
 *    from some callers -- the same rule that keeps `g-visibleWhen` off a schema-required field. A field the schema
 *    withdraws only for some answers (`if`/`then`/`else`) is fine: the requirement is ignored while it is withdrawn.
 */
fun formRequirementProblems(where: String, layout: SchLayout, type: SchType): List<Problem> {
    val problems = mutableListOf<Problem>()
    for ((i, field) in layout.fields.withIndex()) {
        val prop = type.properties[field.field] ?: continue
        val at = "${SL.schemaFields}[$i]"
        field.choices?.let { choices ->
            val offered = prop.valueType.options
            if (offered == null || prop.valueType.openOptions) {
                problems.add(
                    layoutProblem(
                        LayoutError.choicesWithoutOptions,
                        "$where: '${SCH.layout}' gives field '${field.field}' '${SL.choices}', but the schema gives it no " +
                            "closed list of choices to restate.",
                        "$at.${SL.choices}",
                    ),
                )
            } else {
                val values = offered.map { it.value }.toSet()
                choices.forEachIndexed { j, choice ->
                    if (choice.value !in values) {
                        problems.add(
                            layoutProblem(
                                LayoutError.notAChoice,
                                "$where: '${SCH.layout}' offers '${choice.value}' for field '${field.field}', which the " +
                                    "schema does not offer (it offers ${values.joinToString(", ")}).",
                                "$at.${SL.choices}[$j].${SL.value}",
                            ),
                        )
                    }
                }
            }
        }
        if (field.required) {
            val why = when {
                prop.valueType.derived -> "the server derives it"
                prop.visibleWhen != null -> "its '${SCH.visibleWhen}' hides it from some callers"
                else -> null
            }
            why?.let {
                problems.add(
                    layoutProblem(
                        LayoutError.requiredUnfillable,
                        "$where: '${SCH.layout}' requires field '${field.field}', but $it, so the form could not always " +
                            "be saved.",
                        "$at.${SL.required}",
                    ),
                )
            }
        }
    }
    return problems
}

/**
 * The fields of [type] an **authoritative** layout must list ([authoritativeLayoutProblems]): every field the type may
 * require -- [SchType.required] and both sides of its `if`/`then`/`else` -- except a derived one, which nobody at the
 * form supplies. What Design View's field checklist locks on (issue #1071), by the rule the load check holds. Pure.
 */
fun fieldsAListMustShow(type: SchType): Set<String> {
    val condition = type.condition
    val mayRequire = type.required + condition?.thenRequired.orEmpty() + condition?.elseRequired.orEmpty()
    return mayRequire.filterTo(LinkedHashSet()) { type.properties[it]?.valueType?.derived != true }
}

/**
 * The problems with an **authoritative** [layout] against [type] (issue #777): the layout is the whole presented
 * set, so a property the type may require that it omits could never be supplied, and the form is unsubmittable by
 * construction. Empty for any other mode -- reorder and overlay hide nothing, so nothing is owed.
 *
 * "May require" is [SchType.required] **and** both sides of the type's `if`/`then`/`else` (issue #811): a field
 * required only when another field holds some value is still one the form must be able to show. A *derived*
 * property is exempt -- the server supplies it, not the person at the form, so it has no field to list (issue
 * #254).
 *
 * Also run for a client whose narrowing of a type inherits global's layout (issue #811): the narrowing may add to
 * the required set, and an inherited layout is otherwise never checked against the client's type. That re-check is
 * the client-variant build's (`dropFaultyLayouts`), against the layout as pruned to the client's type.
 */
fun authoritativeLayoutProblems(where: String, layout: SchLayout, type: SchType): List<Problem> {
    if (layout.mode != SchLayoutMode.authoritative) return emptyList()
    val listed = layout.fieldNames.toSet()
    return fieldsAListMustShow(type)
        .filter { it !in listed }
        .map {
            val how = if (it in type.required) "" else " (conditionally, through '${SCH.kIf}')"
            layoutProblem(
                LayoutError.omitsRequired,
                "$where: an ${SLM.authoritative} '${SCH.layout}' must list every required field, but omits '$it'$how " +
                    "-- add it, or drop it from '${SCH.required}', or use '${SLM.reorder}'.",
                SL.schemaFields,
            )
        }
}

/**
 * The vocabulary of **form-level layout strings** (issue #641): overridable copy a form shows for the type as a
 * whole, not tied to a single field -- for example the wording over a validation-failure summary. Bare keys
 * inside a `g-layout` block's `strings` map (see [SL.strings]); [keys] is the closed set the parser refuses
 * anything outside of, so a component or client overrides a *known* message and a mistyped name fails the boot
 * rather than silently rendering nothing. The default copy for each lives on the surface that renders it (the
 * frontend); a layout carries only the overrides.
 */
@Suppress("ConstPropertyName")
object LAYSTR {
    /** The heading over a form's internal validation-failure list, shown only when the frontend is in debug
     *  (issue #641). Off debug, the list and this heading are replaced by [formErrorHint]. */
    const val formErrorSummary = "formErrorSummary"

    /** The one-line prompt a form shows in place of the internal failure list when debug is off (issue #641) --
     *  the errors are already marked inline on the fields, so this only points the consumer at them. */
    const val formErrorHint = "formErrorHint"

    /** The line a form shows when the only thing left is to accept supplied defaults (issue #710) -- how many
     *  fields were filled from the caller's account, and that saving keeps them. Templated over `${'$'}{count}` on the
     *  frontend, which also homes the default copy; a layout carries only a client's override of the wording. */
    const val prefillSummary = "prefillSummary"

    /** Every key a `strings` block may carry; the boot check refuses anything outside it. */
    val keys: Set<String> = setOf(formErrorSummary, formErrorHint, prefillSummary)
}

/**
 * The template parameters a layout `${'$'}{…}` may reference, **per context** (issue #587, `thoughts-schema-direction.md`
 * §10). Only the injected-param contexts live here: their vocabulary is fixed and known at boot, so a placeholder
 * naming a param the field's context does not provide is caught like a mistyped key. The field's **own data**
 * (what a `label` / `description` resolves against) is not here -- it is dynamic, so no boot check can enumerate it.
 *
 * The **bounds** context is the first and, in this stage, only one: a numeric/bounded field's `min` / `max`, for a
 * `hint` that replaces the derived `range: X to Y`. A name appears only when the field actually declares that
 * bound, so `${'$'}{max}` on a field with no maximum is a boot failure.
 */
@Suppress("ConstPropertyName")
object LayoutCtx {
    /** The declared lower bound (`minimum` / `minLength` / `minItems`), when the field has one. */
    const val min = "min"

    /** The declared upper bound (`maximum` / `maxLength` / `maxItems`), when the field has one. */
    const val max = "max"
}

/** The bounds-context param names [type] provides: [LayoutCtx.min] when it declares a minimum, [LayoutCtx.max]
 *  when it declares a maximum (issue #587). The boot check holds a `hint` template's references against this. */
fun boundsContextNames(type: SchType): Set<String> = buildSet {
    if (type.minBound != null) add(LayoutCtx.min)
    if (type.maxBound != null) add(LayoutCtx.max)
}

/** The bounds-context data [type] supplies to a `hint` template (issue #587): each provided bound as a
 *  **number**, keyed by [LayoutCtx.min] / [LayoutCtx.max]. Kept numeric rather than pre-formatted so a hint may
 *  compute on it -- `${'$'}{max - min}`, `${'$'}{max > 2000}` -- which string operands would refuse (`numOf` in
 *  `ScriptEval` never coerces a string); a bare `${'$'}{min}` still renders cleanly because `Any?.fmt` trims a whole
 *  value (`2000.0` -> `"2000"`). The same names [boundsContextNames] validates, so the boot check and the render
 *  agree on the vocabulary. */
fun boundsContextData(type: SchType): Map<String, Any?> = buildMap {
    type.minBound?.let { put(LayoutCtx.min, it) }
    type.maxBound?.let { put(LayoutCtx.max, it) }
}

/**
 * The template parameters a layout **error override** message may reference, **per [SchFailCode]** (issue #588,
 * `thoughts-schema-direction.md` §10). This is the vocabulary the design calls "the feature": a `${'$'}{…}` in a
 * `belowMinimum` message may name the bound, an `invalidOption` message the choice list, and so on -- what a
 * message can refer to is decided by *which failure* it is for, not one flat set. The names known at boot
 * ([errorContextNames]) and the data the frontend supplies ([errorContextData]) are defined together, the same
 * pairing (and for the same don't-let-them-drift reason) as [boundsContextNames] / [boundsContextData].
 */
@Suppress("ConstPropertyName")
object LayoutErrCtx {
    /** The offending value, as the field currently holds it. Absent for a failure with no value to show
     *  ([SchFailCode.missingRequired], [SchFailCode.additionalProperty]). */
    const val value = "value"

    /** The property's name -- the one param every failure can offer, and all a `default` message may use. */
    const val field = "field"

    /** The field's valid choices as a readable list, for an [SchFailCode.invalidOption] message. */
    const val options = "options"

    // [LayoutCtx.min] / [LayoutCtx.max] are reused for the bound a `belowMinimum` / `aboveMaximum` message names.
}

/**
 * The param names a layout error message for [code] on field [type] may reference (issue #588) -- what
 * [layoutTemplateProblems] holds an error template's `${'$'}{…}` against, so a placeholder the failure cannot
 * provide (a `${'$'}{max}` for an `invalidOption`, a `${'$'}{value}` for a `missingRequired`) fails the boot like a
 * mistyped key. [code] is null for the `default` key, which may match any failure and so is allowed only the
 * one param present for every one of them ([LayoutErrCtx.field]).
 */
fun errorContextNames(code: SchFailCode?, type: SchType): Set<String> =
    errorContextNames(code, hasMin = type.minBound != null, hasMax = type.maxBound != null)

/**
 * [errorContextNames] from whether the field declares a lower and an upper bound -- the only facts about the type
 * it reads -- so a pass over **raw** definitions (the stored-config repair, issue #841) can ask it too.
 */
fun errorContextNames(code: SchFailCode?, hasMin: Boolean, hasMax: Boolean): Set<String> = buildSet {
    add(LayoutErrCtx.field)
    if (code == null) return@buildSet
    // A value exists for every failure except a property that is missing or one that is not a field at all.
    if (code != SchFailCode.missingRequired && code != SchFailCode.additionalProperty) add(LayoutErrCtx.value)
    when (code) {
        SchFailCode.invalidOption -> add(LayoutErrCtx.options)
        SchFailCode.belowMinimum -> if (hasMin) add(LayoutCtx.min)
        SchFailCode.aboveMaximum -> if (hasMax) add(LayoutCtx.max)
        else -> {}
    }
}

/**
 * The data a layout error message for [code] resolves against (issue #588) -- the runtime counterpart of
 * [errorContextNames], built where the failure is rendered (the frontend). [fieldName] is the property's name,
 * [value] its current value (the offending one), and [options] the failure's choice list. Only the params
 * [errorContextNames] names for [code] are put, so the render and the boot check agree on the vocabulary; a
 * bound comes from [type], the choice list is joined to a readable string. A pure function, defined here beside
 * the names for the same reason [boundsContextData] is.
 */
fun errorContextData(
    code: SchFailCode,
    type: SchType,
    fieldName: String,
    value: Any?,
    options: List<SchOption>?,
): Map<String, Any?> = buildMap {
    put(LayoutErrCtx.field, fieldName)
    if (code != SchFailCode.missingRequired && code != SchFailCode.additionalProperty) {
        value?.let { put(LayoutErrCtx.value, it) }
    }
    when (code) {
        SchFailCode.invalidOption ->
            options?.let { opts -> put(LayoutErrCtx.options, opts.joinToString(", ") { it.label }) }
        SchFailCode.belowMinimum -> type.minBound?.let { put(LayoutCtx.min, it) }
        SchFailCode.aboveMaximum -> type.maxBound?.let { put(LayoutCtx.max, it) }
        else -> {}
    }
}

/**
 * The problems with a layout's copy **templates** -- `label` / `description` / `hint` -- against the field they
 * annotate (issues #587, #605), the boot check for §10's frontend substitution:
 *  - a **malformed** template (an unterminated `${'$'}{...}`, an empty block) fails, on any of the three;
 *  - a **frontend fragment pull** `${'$'}{@t("ns.key")}` fails: a layout pulls a fragment with the backend prefix
 *    `%{@t}`, resolved at delivery (#605), and a frontend one would render as raw text, exactly the "parses
 *    clean, renders wrong" the layout boot checks exist to prevent;
 *  - a **hint** additionally may reference only its field's bounds params: a `${'$'}{max}` on a field with no maximum
 *    fails, like a mistyped key. A `label` / `description` resolves against the field's own **dynamic** data, so
 *    its `${'$'}{...}` data paths are not checked here (no boot can enumerate them).
 * [type] is the object the layout annotates; a field it does not declare is reported by [layoutFieldProblems]
 * and skipped here.
 */
fun layoutTemplateProblems(where: String, layout: SchLayout, type: SchType?): List<Problem> {
    if (type == null) return emptyList()
    val problems = mutableListOf<Problem>()
    fun malformed(at: String, message: String, issue: TemplateIssue) =
        problems.add(layoutProblem(LayoutError.malformedTemplate, message, at, issue))
    fun add(code: LayoutError, at: String, message: String) = problems.add(layoutProblem(code, message, at))
    // The block-level name and introduction (issues #605, #1534): copy strings like any other, but not a field's, so
    // they take the malformed and refuse-frontend-pull checks and not the bounds check.
    val blockCopy = listOf(Triple(SL.label, "name", layout.label), Triple(SL.description, "introduction", layout.description))
    for ((key, what, text) in blockCopy) {
        val analysis = (text ?: continue).analyzeTemplate()
        for (issue in analysis.issues) {
            malformed(key, "$where: the '${SCH.layout}' $what is a malformed template: ${issue.message}", issue)
        }
        if (analysis.refs.isNotEmpty()) {
            add(
                LayoutError.frontendPull, key,
                $$"$$where: the '$${SCH.layout}' $$what uses a frontend fragment pull ('${@t}'); a layout fragment " +
                    "pull uses the backend prefix '%{@t}', resolved at delivery (see #605).",
            )
        }
    }
    // Each form-level string (issue #641) is copy like the heading: no field context, so it takes the malformed
    // and refuse-frontend-pull checks and no param check. A `%{@t}` backend pull is resolved at delivery (#605).
    for ((key, text) in layout.strings) {
        val analysis = text.analyzeTemplate()
        val at = "${SL.strings}.$key"
        for (issue in analysis.issues) {
            malformed(at, "$where: the '${SCH.layout}' string '$key' is a malformed template: ${issue.message}", issue)
        }
        if (analysis.refs.isNotEmpty()) {
            add(
                LayoutError.frontendPull, at,
                $$"$$where: the '$${SCH.layout}' string '$$key' uses a frontend fragment pull ('${@t}'); a layout " +
                    "fragment pull uses the backend prefix '%{@t}', resolved at delivery (see #605).",
            )
        }
    }
    for ((i, field) in layout.fields.withIndex()) {
        val prop = type.properties[field.field] ?: continue
        for ((kind, text) in listOf(SL.label to field.label, SL.description to field.description, SL.hint to field.hint)) {
            if (text == null) continue
            val at = "${SL.schemaFields}[$i].$kind"
            val analysis = text.analyzeTemplate()
            for (issue in analysis.issues) {
                malformed(
                    at,
                    "$where: the '${SCH.layout}' $kind for '${field.field}' is a malformed template: " + issue.message,
                    issue,
                )
            }
            if (analysis.refs.isNotEmpty()) {
                add(
                    LayoutError.frontendPull, at,
                    $$"$$where: the '$${SCH.layout}' $$kind for '$${field.field}' uses a frontend fragment pull ('${@t}'); " +
                        "a layout fragment pull uses the backend prefix '%{@t}', resolved at delivery (see #605).",
                )
            }
            if (kind == SL.hint) {
                val allowed = boundsContextNames(prop.valueType)
                for (path in analysis.paths.required + analysis.paths.optional) {
                    val name = path.substringBefore('.')
                    if (name !in allowed) {
                        add(
                            LayoutError.unknownParam, at,
                            $$"$$where: the '$${SCH.layout}' hint for '$${field.field}' references '${$$path}', but this " +
                                "field's bounds context provides ${if (allowed.isEmpty()) "no params (it declares no minimum or maximum)" else allowed.sorted().joinToString(", ")}.",
                        )
                    }
                }
            }
        }
        // The error override (issue #588): each message is a frontend `${'$'}{…}` template over its failure code's
        // params. Its keys are already validated against the code enum by `parseErrorMessages`; here the message
        // itself is checked -- malformed, a fragment pull (not supported in error copy this stage), or a `${'$'}{…}`
        // the failure cannot provide -- the same "parses clean, renders wrong" the other copy checks prevent.
        for ((codeKey, message) in field.errors) {
            // A valid key is a SchFailCode name or `default`; `default` (code == null) may match any failure.
            val code = SchFailCode.entries.firstOrNull { it.name == codeKey }
            val at = "${SL.schemaFields}[$i].${SL.errors}.$codeKey"
            val analysis = message.analyzeTemplate()
            for (issue in analysis.issues) {
                malformed(
                    at,
                    "$where: the '${SCH.layout}' error '$codeKey' for '${field.field}' is a malformed template: " +
                        issue.message,
                    issue,
                )
            }
            if (analysis.refs.isNotEmpty()) {
                add(
                    LayoutError.frontendPull, at,
                    $$"$$where: the '$${SCH.layout}' error '$$codeKey' for '$${field.field}' uses a fragment pull ('${@t}'); " +
                        "a layout error message supports only frontend parameter substitution (see #588).",
                )
            }
            val allowed = errorContextNames(code, prop.valueType)
            for (path in analysis.paths.required + analysis.paths.optional) {
                val name = path.substringBefore('.')
                if (name !in allowed) {
                    add(
                        LayoutError.unknownParam, at,
                        $$"$$where: the '$${SCH.layout}' error '$$codeKey' for '$${field.field}' references '${$$path}', but a " +
                            $$"'$$codeKey' failure provides $${allowed.sorted().joinToString(", ")}.",
                    )
                }
            }
        }
    }
    return problems
}

/**
 * What can be wrong with a `g-layout` block (issue #909): the codes of its parse ([parseSchLayoutResult],
 * [parseTypeLayout]) and of the boot checks run against it. Each problem is located within the block --
 * `schemaFields[2].hint`, `strings.formErrorHint`, `schemaFields[0].errors.maximum` -- and a template fault adds
 * its offset in the copy. The message is the same sentence as ever, naming the type.
 */
@Suppress("EnumEntryName")
enum class LayoutError : ProblemCode {
    /** A key the block, a field entry or `strings` does not know -- usually a typo. */
    unknownKey,

    /** A block with no `schemaFields` entries, which would parse clean and render nothing. */
    noFields,

    /** A `schemaFields` entry with no `field`. */
    noFieldName,

    /** A value of the wrong shape or outside its closed set: a `mode`, a `defaultMode`, a `strings` value. */
    badValue,

    /** A `g-layout` below a type's top level, where there is no type name to key it by. */
    nestedLayout,

    /** A layout on a type that did not resolve. */
    unresolvedType,

    /** A layout on a union or an array, which has no single property set to render. */
    notAnObjectType,

    /** A field the type does not declare. */
    undeclaredField,

    /** An authoritative layout leaving out a field the type may require, so the form could not be submitted. */
    omitsRequired,

    /** A field entry's `choices` on a field the schema gives no closed list of choices (issue #1022). */
    choicesWithoutOptions,

    /** A field entry's `choices` naming a value the schema does not offer (issue #1022). */
    notAChoice,

    /** A field entry's `required` on a field nobody at the form could always fill in (issue #1022). */
    requiredUnfillable,

    /** A field entry's `multiline` on a field that is not plain text (issue #1118). */
    multilineNotText,

    /** Copy whose `${'$'}{…}` or `%{…}` template does not parse. */
    malformedTemplate,

    /**
     * A frontend `${'$'}{@t}` pull, where a layout pulls with the backend `%{@t}` -- or a pull where none is allowed.
     */
    frontendPull,

    /** A template parameter its context does not provide: `${'$'}{max}` on a field with no maximum. */
    unknownParam,

    /** A backend `%{…}` block in error copy, which delivery does not resolve. */
    backendBlock,

    /** A backend pull whose key is not a `fileId.namespace.key` reference. */
    badPullKey,

    /** A backend pull naming a fragment file that is not declared. */
    pullFileMissing,

    /** A backend pull naming a frontend fragment file. */
    pullFromFrontendFile,

    /** A backend pull naming a key its file does not have. */
    pullKeyMissing,
}

/**
 * A [LayoutError] problem at [path] within the `g-layout` block (null for the block as a whole), with a template
 * [issue]'s offset, line, and column when the fault is inside copy.
 */
@KdrPrivate
fun layoutProblem(code: LayoutError, message: String, path: String? = null, issue: TemplateIssue? = null): Problem =
    Problem(
        code,
        message,
        if (path == null && issue == null) null else ProblemLocation(path, issue?.offset, issue?.line, issue?.col),
    )
